import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import ForgotPasswordModal from './ForgotPasswordModal';

const { FiMail, FiLock, FiEye, FiEyeOff, FiUser, FiKey, FiArrowRight } = FiIcons;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    await performLogin(email, password);
  };

  const performLogin = async (loginEmail, loginPassword) => {
    setLoading(true);
    setLoginMethod('email');
    try {
      await login(loginEmail, loginPassword);
      toast.success('Welcome back!');
      setTimeout(() => navigate('/dashboard'), 500);
    } catch (error) {
      toast.error('Login failed. Please try again.');
      setLoginMethod(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDemoOwner = () => {
    setLoginMethod('owner');
    setEmail('demo@owner.com');
    setPassword('password');
    setTimeout(() => performLogin('demo@owner.com', 'password'), 300);
  };

  const handleDemoRenter = () => {
    setLoginMethod('renter');
    setTimeout(() => navigate('/portal/demo-guest'), 300);
  };

  return (
    <div className="min-h-screen bg-[#0b1e3b] flex items-center justify-center px-4 py-12 relative overflow-hidden">
      {/* Abstract Background Shapes */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-purple-600/20 rounded-full blur-3xl"></div>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full relative z-10"
      >
        <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-10 border border-white/10">
          <div className="text-center mb-8">
            <img 
              src="https://dapperdev.com/wp-content/uploads/2025/11/anjo-logos_0000_anjo-properties-copy.png" 
              alt="Anjo Properties" 
              className="h-24 w-auto mx-auto mb-6 object-contain"
            />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h1>
            <p className="text-gray-500">Sign in to manage your properties</p>
          </div>

          {/* Quick Access Grid */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <button
              onClick={handleDemoOwner}
              disabled={loading}
              className={`p-4 rounded-2xl border-2 transition-all duration-200 flex flex-col items-center justify-center group ${
                loginMethod === 'owner' 
                  ? 'border-blue-600 bg-blue-50' 
                  : 'border-gray-100 bg-gray-50 hover:border-blue-200 hover:bg-white'
              }`}
            >
              <SafeIcon icon={FiUser} className={`text-2xl mb-2 ${loginMethod === 'owner' ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-600'}`} />
              <span className={`text-sm font-semibold ${loginMethod === 'owner' ? 'text-blue-900' : 'text-gray-600'}`}>Demo Owner</span>
            </button>

            <button
              onClick={handleDemoRenter}
              disabled={loading}
              className={`p-4 rounded-2xl border-2 transition-all duration-200 flex flex-col items-center justify-center group ${
                loginMethod === 'renter' 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-gray-100 bg-gray-50 hover:border-green-200 hover:bg-white'
              }`}
            >
              <SafeIcon icon={FiKey} className={`text-2xl mb-2 ${loginMethod === 'renter' ? 'text-green-600' : 'text-gray-400 group-hover:text-green-600'}`} />
              <span className={`text-sm font-semibold ${loginMethod === 'renter' ? 'text-green-900' : 'text-gray-600'}`}>Demo Renter</span>
            </button>
          </div>

          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
            <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-gray-400">or sign in with email</span></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <SafeIcon icon={FiMail} className="text-gray-400" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                  placeholder="name@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-gray-700">Password</label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <SafeIcon icon={FiLock} className="text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-11 pr-12 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                </button>
              </div>
            </div>

            <motion.button
              type="submit"
              disabled={loading || loginMethod !== null}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 px-4 rounded-xl shadow-lg shadow-blue-200 transition-all duration-200 flex items-center justify-center space-x-2"
            >
              {loading && loginMethod === 'email' ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <span>Sign In</span>
                  <SafeIcon icon={FiArrowRight} />
                </>
              )}
            </motion.button>
          </form>

          <p className="mt-8 text-center text-gray-500 text-sm">
            Don't have an account?{' '}
            <Link to="/signup" className="text-blue-600 font-semibold hover:text-blue-700">Sign up</Link>
          </p>
        </div>
      </motion.div>

      {showForgotPassword && (
        <ForgotPasswordModal
          onClose={() => setShowForgotPassword(false)}
        />
      )}
    </div>
  );
};

export default Login;